/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FileOutlined,
  LoadingOutlined,
  NotificationOutlined,
  HomeOutlined,
  UserOutlined,
  CarOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { Badge, notification } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { ItemType } from 'antd/es/menu/hooks/useItems'
import { MenuItemType } from 'antd/lib/menu/hooks/useItems'

import { PATHS, WebTitleEnum } from '@types'
import {
  useConfigurationProperties,
  useGetConfigurationsData,
  useLogout,
  usePendings,
} from '@hooks'
import { useFilters, useGeneral } from '@contexts'

import { StyledMenu } from '../../../components/StyledComponents'

const CarrierMenu: () => JSX.Element = () => {
  const navigate = useNavigate()
  const { logout } = useLogout()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { data, isLoading } = usePendings()
  const { setTicketToSearch } = useGeneral()
  const [notified, setNotified] = useState(false)
  const [requests, setRequests] = useState(0)
  const { resetFilters } = useFilters()
  const { carrierAvailabilityIsEnabled } = useConfigurationProperties()

  const { dataConfiguration } = useGetConfigurationsData()
  let tokenExpirationSeconds: number

  const clearFiltersOnComponentChange = () => {
    setTicketToSearch(undefined)
    resetFilters()
  }

  const handleClickPendings = () => {
    clearFiltersOnComponentChange()
    navigate(PATHS.CARRIER.HOME)
  }

  const handleClickAll = () => {
    clearFiltersOnComponentChange()
    navigate(PATHS.CARRIER.ALL)
  }
  const handleClickEvouchers = () => {
    clearFiltersOnComponentChange()
    navigate(PATHS.CARRIER.EVOUCHERS)
  }
  const handleClickVehicles = () => {
    clearFiltersOnComponentChange()
    navigate(PATHS.CARRIER.VEHICLES)
  }

  const handleClickDrivers = () => {
    clearFiltersOnComponentChange()
    navigate(PATHS.CARRIER.DRIVERS)
  }

  const handleClickBatches = () => {
    clearFiltersOnComponentChange()
    navigate(PATHS.CARRIER.BATCHES)
  }

  const handleClickAvailability = () => {
    clearFiltersOnComponentChange()
    navigate(PATHS.CARRIER.AVAILABILITY)
  }

  const items = useMemo(
    () =>
      [
        {
          key: 'submen1',
          label: t('tpl_router.menu.requests'),
          open: true,
          children: [
            {
              key: PATHS.CARRIER.HOME,
              onClick: handleClickPendings,
              icon: <HomeOutlined />,
              label: (
                <>
                  {t('tpl_router.menu.pendings')}{' '}
                  {isLoading ? (
                    <LoadingOutlined />
                  ) : (
                    <>
                      {data && data > 0 ? (
                        <Badge className="menu-badge" count={data}>
                          <NotificationOutlined style={{ cursor: 'pointer' }} />
                        </Badge>
                      ) : (
                        ''
                      )}
                    </>
                  )}
                </>
              ),
            },
            {
              key: PATHS.CARRIER.ALL,
              onClick: handleClickAll,
              icon: <HistoryOutlined />,
              label: t('tpl_router.menu.all'),
            },
          ],
        },
        {
          key: PATHS.CARRIER.EVOUCHERS,
          onClick: handleClickEvouchers,
          icon: <FileOutlined />,
          label: t('tpl_router.menu.evouchers'),
        },
        {
          key: PATHS.CARRIER.BATCHES,
          onClick: handleClickBatches,
          icon: <AppstoreOutlined />,
          label: t('tpl_router.menu.batches'),
        },
        {
          key: PATHS.CARRIER.VEHICLES,
          onClick: handleClickVehicles,
          icon: <CarOutlined />,
          label: t('tpl_router.menu.vehicles'),
        },
        {
          key: PATHS.CARRIER.DRIVERS,
          onClick: handleClickDrivers,
          icon: <UserOutlined />,
          label: t('tpl_router.menu.drivers'),
        },
        !!carrierAvailabilityIsEnabled && {
          key: PATHS.CARRIER.AVAILABILITY,
          onClick: handleClickAvailability,
          icon: <CheckCircleOutlined />,
          label: t('tpl_router.menu.availability'),
        },
      ].filter(item => !!item),
    [
      t,
      handleClickPendings,
      isLoading,
      data,
      handleClickAll,
      handleClickEvouchers,
      handleClickBatches,
      handleClickVehicles,
      handleClickDrivers,
      carrierAvailabilityIsEnabled,
      handleClickAvailability,
    ],
  )

  useEffect(() => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < dataConfiguration?.configurationProperties.length; i++) {
      if (dataConfiguration?.configurationProperties[i].key === 'TOKEN_EXPIRATION_SECONDS') {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        tokenExpirationSeconds = dataConfiguration?.configurationProperties[i].value
        break
      }
    }

    setTimeout(() => {
      if (tokenExpirationSeconds !== undefined) {
        logout()
      }
    }, (tokenExpirationSeconds - 1) * 1000)
  }, [dataConfiguration])

  useEffect(() => {
    document.title = `${!data ? WebTitleEnum.TITLE : `${`(${data})`} ${WebTitleEnum.TITLE}`}`
  }, [data])

  useEffect(() => {
    if (!notified) {
      if (data) {
        notification.info({
          message:
            // eslint-disable-next-line no-nested-ternary
            data > 0
              ? data === 1
                ? `${t('notifications.preffix')} una ${t(
                    'globals.generics.pending_request',
                  ).toLowerCase()}`
                : `${t('notifications.preffix')} ${data} ${t(
                    'globals.generics.pending_requests',
                  ).toLowerCase()}`
              : `${t('preffix_not')} ${t('globals.generics.pending_requests').toLowerCase()}`,
          duration: 8,
        })
        setNotified(true)
        setRequests(data)
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (data && data > requests && data - requests > 1) {
        notification.info({
          message: `Tiene ${data} solicitudes pendientes`,
          duration: 8,
        })
        setRequests(data)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  return (
    <StyledMenu
      selectedKeys={[pathname]}
      defaultOpenKeys={['submen1', PATHS.CARRIER.HOME]}
      mode="inline"
      items={items as ItemType<MenuItemType>[]}
    />
  )
}

export { CarrierMenu }
